// Libraries
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { FileSearch02 } from "@untitled-ui/icons-react";

// Components
import ConfigSectionWrapperV2 from "#src/commonV2/helper/wrapper/ConfigSection";
import { FormInputV2 } from "#src/commonV2/form/Input";
import { FormTextArea } from "#src/commonV2/form/TextArea";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import { ButtonV2 } from "#src/commonV2/Button";

// API-related
import resourceServices from "#src/services/resource";
import { FormInputLeadingDropdown } from "#src/commonV2/form/InputLeadingDropdown/FormInputLeadingDropdown";

export interface IWorkspaceFormBasic {
  name: string;
  email: string;
  short_name: string;
  phone: string;
  vat_tax_code: string;
  address: string;
  website: string;
  description: {
    en: string;
    vi: string;
  };
  country_code: string;
  country_phone_code?: string;
}

interface IBasicInformationProps {
  isError: boolean;
  callingAPI: boolean;
  allowedToUpdate: boolean;
  showSaveButton: boolean;
  information: IWorkspaceFormBasic;
  getFieldError: (fieldKey: string, formData: any) => string | null;
  onChangeInformation: Dispatch<SetStateAction<IWorkspaceFormBasic>>;
  onSaveChanges: () => void;
}
const WorkspaceBasicInformation = ({
  isError,
  callingAPI = false,
  allowedToUpdate,
  showSaveButton,
  information,
  getFieldError,
  onChangeInformation,
  onSaveChanges,
}: IBasicInformationProps) => {
  const { t } = useTranslation("workspace", { keyPrefix: "detail" });

  const [countries, setCountries] = useState<IFilterItemV2[]>([]);

  const selectedCountryCode: IFilterItemV2 = useMemo(() => {
    return (
      countries.find((c) => c.key === information.country_code) || {
        key: "VN",
        value: "+84",
        getLabel: () => {
          return "+84";
        },
      }
    );
  }, [countries, information.country_code]);

  const disabled = useMemo(() => {
    return !allowedToUpdate || callingAPI;
  }, [allowedToUpdate, callingAPI]);

  useEffect(() => {
    fetchCountry();
  }, []);

  useEffect(() => {
    if (selectedCountryCode) {
      const phoneCode = selectedCountryCode.value;
      if (phoneCode && information.phone.includes(phoneCode)) {
        onChangeInformation({
          ...information,
          phone: information.phone.replace(phoneCode, "")?.trim(),
        });
      }
    }
  }, [information, selectedCountryCode, onChangeInformation]);

  const fetchCountry = async () => {
    const response = await resourceServices.list_countries();
    const result: IFilterItemV2[] = response.map((item) => {
      let filterItem: IFilterItemV2 = {
        key: item.country_code,
        value: item.country_phone_code,
        leftIcon: (
          <img
            src={`/country-flag/${item.country_code.toLowerCase()}.svg`}
            className="w-[1.125rem] h-[1.125rem] object-cover rounded-[3px]"
          />
        ),
        getLabel: () => {
          return item.country_phone_code;
        },
      };
      return filterItem;
    });
    setCountries(result);
  };

  return (
    <ConfigSectionWrapperV2
      icon={<FileSearch02 className="h-5 w-5 text-gray-v2-500" />}
      headerTitle={t("basicInformation.title")}
      headerButton={
        allowedToUpdate && showSaveButton ? (
          <ButtonV2
            variant="secondary"
            disabled={isError}
            onClick={onSaveChanges}
          >
            {t("button.saveChanges")}
          </ButtonV2>
        ) : (
          <></>
        )
      }
    >
      <div className="grid grid-cols-2 gap-8 p-4">
        <div className="flex flex-col gap-6">
          <FormTextArea
            label={t("basicInformation.form.companyName")}
            placeholder={t("basicInformation.form.placeholderCompanyName")}
            value={information.name}
            required
            rows={1}
            disabled={disabled}
            error={getFieldError("name", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, name: v })
            }
          />
          <FormInputV2
            label={t("basicInformation.form.shortName")}
            placeholder={t("basicInformation.form.placeholderShortName")}
            value={information.short_name}
            disabled={disabled}
            error={getFieldError("short_name", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, short_name: v })
            }
          />
          <FormTextArea
            label={t("basicInformation.form.descriptionEN")}
            placeholder={t("basicInformation.form.placeholderDescription")}
            value={information.description.en}
            disabled={disabled}
            error={getFieldError("description.en", information)}
            onChangeValue={(v) =>
              onChangeInformation({
                ...information,
                description: { en: v, vi: information.description.vi },
              })
            }
          />
          <FormTextArea
            label={t("basicInformation.form.descriptionVI")}
            placeholder={t("basicInformation.form.placeholderDescription")}
            value={information.description.vi}
            disabled={disabled}
            error={getFieldError("description.vi", information)}
            onChangeValue={(v) =>
              onChangeInformation({
                ...information,
                description: { vi: v, en: information.description.en },
              })
            }
          />
        </div>
        <div className="flex flex-col gap-6">
          <FormInputV2
            label={t("basicInformation.form.homepage")}
            placeholder={t("basicInformation.form.placeholderHomepage")}
            value={information.website}
            required
            disabled={disabled}
            error={getFieldError("website", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, website: v })
            }
          />
          <FormTextArea
            label={t("basicInformation.form.address")}
            placeholder={t("basicInformation.form.placeholderAddress")}
            value={information.address}
            disabled={disabled}
            error={getFieldError("address", information)}
            rows={3}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, address: v })
            }
          />
          <FormInputV2
            label={t("basicInformation.form.vatTaxCode")}
            placeholder={t("basicInformation.form.placeholderVatTaxCode")}
            value={information.vat_tax_code}
            disabled={disabled}
            error={getFieldError("vat_tax_code", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, vat_tax_code: v })
            }
          />
          <FormInputV2
            label={t("basicInformation.form.email")}
            placeholder={t("basicInformation.form.placeholderEmail")}
            value={information.email}
            disabled={disabled}
            error={getFieldError("email", information)}
            onChangeValue={(v) =>
              onChangeInformation({ ...information, email: v })
            }
          />
          <FormInputLeadingDropdown
            id="country-phone-code"
            label={t("basicInformation.form.phone")}
            value={information.phone}
            placeholder={t("basicInformation.form.placeholderPhone")}
            selected={selectedCountryCode}
            onChange={(e) =>
              onChangeInformation({ ...information, phone: e.target.value })
            }
            options={countries}
            errorText={getFieldError("phone", information)}
            onChangeSelected={(v) =>
              onChangeInformation({
                ...information,
                country_code: v.key,
                country_phone_code: v.value,
              })
            }
            disabled={disabled}
          />
        </div>
      </div>
    </ConfigSectionWrapperV2>
  );
};

export default WorkspaceBasicInformation;
